import { useLogRocket } from 'hooks/useLogRocket';
import { useEffect } from 'react';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import LogRocketService from 'services/logRocketService';

// Extend Window interface for LogRocket debug methods
declare global {
  interface Window {
    testLogRocket?: () => void;
    logRocketStatus?: () => ReturnType<typeof LogRocketService.prototype.getStatus>;
    logRocketSessionURL?: () => string | null;
  }
}

/**
 * LogRocketInitializer Component
 *
 * Initializes LogRocket at the application level
 * This component should be placed high in the component tree (e.g., in App.tsx)
 *
 * Features:
 * - Initializes LogRocket on mount
 * - Logs initialization status
 * - Exposes debug methods globally for testing
 * - No UI rendering (returns null)
 *
 * Usage:
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <LogRocketInitializer />
 *       <YourAppContent />
 *     </>
 *   );
 * }
 * ```
 *
 * Testing:
 * Open browser console and run:
 * - window.testLogRocket() - Test LogRocket functionality
 * - window.logRocketStatus() - Get current status
 * - window.logRocketSessionURL() - Get session URL
 */
const LogRocketInitializer = () => {
  useLogRocket();
  const logRocketService = LogRocketService.getInstance();

  useEffect(() => {
    // Wait a bit for LogRocket to fully initialize
    const timer = setTimeout(() => {
      const status = logRocketService.getStatus();
      console.log('🔍 LogRocket Initialization Status:', status);

      // Check status directly from service, not from hook state
      const isServiceActive = logRocketService.isActive;

      if (isServiceActive) {
        console.log('✅ LogRocket is active and monitoring this session');
        console.log('📊 Session URL will be available in a few seconds');

        // Link LogRocket session to Sentry so every Sentry issue has a replay link
        LogRocket.getSessionURL(sessionURL => {
          Sentry.setTag('logrocket_session', sessionURL);
          console.log('🔗 LogRocket session linked to Sentry:', sessionURL);
        });

        // Expose utility methods globally for debugging
        window.testLogRocket = () => {
          console.log('🧪 Testing LogRocket...');
          logRocketService.testLogRocket();
        };

        window.logRocketStatus = () => {
          const currentStatus = logRocketService.getStatus();
          console.log('📊 LogRocket Status:', currentStatus);
          return currentStatus;
        };

        window.logRocketSessionURL = () => {
          const url = logRocketService.getSessionURL();
          console.log('🔗 LogRocket Session URL:', url);
          return url;
        };

        console.log('🧪 Debug commands available in console:');
        console.log('  - window.testLogRocket()        - Test LogRocket');
        console.log('  - window.logRocketStatus()      - Get status');
        console.log('  - window.logRocketSessionURL()  - Get session URL');
      } else {
        console.log(
          '❌ LogRocket is disabled - no session monitoring',
          '\nTo enable LogRocket:',
          '\n1. Set VITE_LOGROCKET_APP_ID environment variable',
          '\n2. Or configure window.config.logRocketAppId'
        );
      }
    }, 100); // Small delay to ensure initialization is complete

    // Cleanup on unmount
    return () => {
      clearTimeout(timer);
      // Remove global debug methods
      delete window.testLogRocket;
      delete window.logRocketStatus;
      delete window.logRocketSessionURL;
    };
  }, [logRocketService]);

  // This component doesn't render anything
  return null;
};

export default LogRocketInitializer;
